import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { IntercomModule } from 'ng-intercom';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { NgbCarouselModule, NgbModule } from '@ng-bootstrap/ng-bootstrap';

import { DatePipe } from '@angular/common';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { HAMMER_GESTURE_CONFIG, HammerModule } from '@angular/platform-browser';
import { AdsBannerModule } from '@shared/ads-banner/ads-banner.module';
import { AdItemBannerModule } from '@shared/components/ad-item-banner/ad-item-banner.module';
import { AdItemModule } from '@shared/components/ad-item/ad-item.module';
import { ButtonModule } from '@shared/components/button/button.module';
import firebase from 'firebase/compat/app';
import { CookieService } from 'ngx-cookie-service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ShareButtonsModule } from 'ngx-sharebuttons/buttons';
import { ShareIconsModule } from 'ngx-sharebuttons/icons';
import { CoreModule } from './core/core.module';
import { DeviceDetectComponent } from './device-detect/device-detect.component';
import { FooterComponent } from './footer/footer.component';
import { HomeHeadComponent } from './home-head/home-head.component';
import { InnerFooterComponent } from './inner-footer/inner-footer.component';
import { NavigationComponent } from './navigation/navigation.component';
import { PlatformUpdatesComponent } from './platform-updates/platform-updates.component';
import { AboutUsComponent } from './routes/about-us/about-us.component';
import { AdsAnalyticsComponent } from './routes/ads-analytics/ads-analytics.component';
import { AdComponent, FullscreenCarouselDialog } from './routes/ads/ad/ad.component';
import { AddViewRepliesComponent } from './routes/ads/ad/add-view-replies/add-view-replies.component';
import { ViewCommentsComponent } from './routes/ads/ad/view-comments/view-comments.component';
import { AdsComponent } from './routes/ads/ads.component';
import { AppsComponent } from './routes/apps/apps.component';
import { AuthDialogModule } from './routes/auth/auth-dialog/auth-dialog.module';
import { AuthLayoutModule } from './routes/auth/auth-layout/auth-layout.module';
import { BarcodeScannerComponent } from './routes/barcode-scanner/barcode-scanner.component';
import { CampaignComponent } from './routes/campaign/campaign.component';
import { HelpTopicComponent } from './routes/help/help-topic/help-topic.component';
import { HelpComponent } from './routes/help/help.component';
import { HomeComponent } from './routes/home/home.component';
import { LogupComponent } from './routes/logup/logup.component';
import { PageNotFoundComponent } from './routes/page-not-found/page-not-found.component';
import { PaymentTempComponent } from './routes/payment-temp/payment-temp.component';
import { PaymentFailComponent } from './routes/payment/payment-fail/payment-fail.component';
import { PaymentSuccessComponent } from './routes/payment/payment-success/payment-success.component';
import { PaymentComponent } from './routes/payment/payment.component';
import { PlaceAdStepperComponent } from './routes/place-ad/place-ad-stepper/place-ad-stepper.component';
import { PlaceAdSuccessComponent } from './routes/place-ad/place-ad-success/place-ad-success.component';
import { PlaceAdComponent } from './routes/place-ad/place-ad.component';
import { PremiumComponent } from './routes/premium/premium.component';
import { PremvipTabComponent } from './routes/premvip-tab/premvip-tab.component';
import { PricingComponent } from './routes/pricing/pricing.component';
import { UserAdsComponent } from './routes/user/user-ads/user-ads.component';
import { UserEditAdComponent } from './routes/user/user-edit-ad/user-edit-ad.component';
import { UserProcessComponent } from './routes/user/user-process/user-process.component';
import { UserProfileChangePhoneComponent } from './routes/user/user-profile/user-profile-change-phone/user-profile-change-phone.component';
import { UserProfileComponent } from './routes/user/user-profile/user-profile.component';
import { UserResetPasswordComponent } from './routes/user/user-reset-password/user-reset-password.component';
import { UserVerifyComponent } from './routes/user/user-verify/user-verify.component';
import { VipComponent } from './routes/vip/vip.component';
import { AuthInterceptor } from './shared/interceptors/auth.interceptor';
import { BarcodeShareComponent } from './shared/barcode-share/barcode-share.component';
import { BarcodeViewComponent } from './shared/barcode/barcode-view/barcode-view.component';
import { FilterModule } from './shared/components/filter/filter.module';
import { GoogleAdModule } from './shared/components/google-ad/google-ad.module';
import { ConfirmationDialogComponent } from './shared/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationOtpComponent } from './shared/confirmation-otp/confirmation-otp.component';
import { AllownumberonlyDirective, ObserveElementDirective, ReadMoreDirective } from './shared/directives';
import { SelectLanguageComponent } from './shared/select-language/select-language.component';
import { BoostAdDialogModule } from '@shared/components/boost-ad-dialog/boost-ad-dialog.module';
import { PaymentProviderComponent } from './routes/payment/payment-provider/payment-provider.component';
import { PaymentCodeComponent } from './routes/payment/payment-code/payment-code.component';
import { TransferCompleteDialogComponent } from './routes/payment/transfer-complete-dialog/transfer-complete-dialog.component';
import { getToken, initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { MissedCallsComponent } from './routes/missed-calls/missed-calls.component';
import { SharedModule } from './shared/shared.module';
import { HammerconfigService } from '@shared/services/hammerconfig.service';
import { AdItemV2Module } from '@shared/components/ad-item-v2/ad-item-v2.module';
import { CompanyPagesComponent } from './routes/company-pages/company-pages.component';
import { PaymentConfirmComponent } from './routes/payment/payment-confirm/payment-confirm.component';
import { PaymentTierComponent } from './routes/payment/payment-tier/payment-tier.component';
import { VipPlusComponent } from './routes/vip-plus/vip-plus.component';
import { VideoModalModule } from '@shared/components/video-modal/video-modal.module';
import { CustomerFeedbackComponent } from './routes/customer-feedback/customer-feedback.component';
import { UserPreferencesComponent } from './routes/user/user-preferences/user-preferences.component';

const APP = firebase.initializeApp(environment.FIREBASE);

const APP_CHECK = initializeAppCheck(APP, {
	provider: new ReCaptchaEnterpriseProvider(environment.FIREBASE.recaptchaEnterpriseKey),
	isTokenAutoRefreshEnabled: true, // Set to true to allow auto-refresh.
});

// console.log(APP_CHECK, 'APP_CHECK');

// // * If set to true, enables automatic background refresh of App Check token.

// const callApiWithAppCheckExample = async () => {
// 	let appCheckTokenResponse;
// 	console.log('1');

// 	try {
// 		console.log('2');

// 		appCheckTokenResponse = await getToken(APP_CHECK, /* forceRefresh= */ false);
// 		console.log('3');
// 		console.log(appCheckTokenResponse, 'appCheckTokenResponse after');

// 		localStorage.setItem('appCheckToken', appCheckTokenResponse.token);
// 	} catch (err) {
// 		console.log(err, 'err app check');

// 		// Handle any errors if the token was not retrieved.
// 		return;
// 	}
// };

// callApiWithAppCheckExample();

const directives = [AllownumberonlyDirective, ObserveElementDirective, ReadMoreDirective];

@NgModule({
	declarations: [
		...directives,
		AppComponent,
		NavigationComponent,
		FooterComponent,
		ConfirmationDialogComponent,
		HomeComponent,
		LogupComponent,
		AdsComponent,
		AdComponent,
		FullscreenCarouselDialog,
		UserProfileComponent,
		UserAdsComponent,
		PlaceAdComponent,
		UserEditAdComponent,
		PlaceAdStepperComponent,
		PlaceAdSuccessComponent,
		PremiumComponent,
		VipComponent,
		PricingComponent,
		PaymentComponent,
		HelpComponent,
		HelpTopicComponent,
		AppsComponent,
		PageNotFoundComponent,
		PaymentSuccessComponent,
		UserVerifyComponent,
		UserProcessComponent,
		UserProfileChangePhoneComponent,
		UserResetPasswordComponent,
		CampaignComponent,
		PaymentTempComponent,
		ConfirmationOtpComponent,
		DeviceDetectComponent,
		ViewCommentsComponent,
		AddViewRepliesComponent,
		PlatformUpdatesComponent,
		AdsAnalyticsComponent,
		PaymentFailComponent,
		HomeHeadComponent,
		PremvipTabComponent,
		AboutUsComponent,
		BarcodeViewComponent,
		BarcodeScannerComponent,
		BarcodeShareComponent,
		SelectLanguageComponent,
		InnerFooterComponent,
		PaymentProviderComponent,
		PaymentCodeComponent,
		TransferCompleteDialogComponent,
		MissedCallsComponent,
		PaymentTierComponent,
		PaymentConfirmComponent,
		VipPlusComponent,
		CompanyPagesComponent,
		CustomerFeedbackComponent,
  UserPreferencesComponent,
	],
	providers: [
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthInterceptor,
			multi: true,
		},
		{
			provide: HAMMER_GESTURE_CONFIG,
			useClass: HammerconfigService,
		},
		DeviceDetectorService,
		CookieService,
		DatePipe,
	],
	bootstrap: [AppComponent],
	imports: [
		CoreModule,
		FlexLayoutModule,
		BrowserModule,
		AppRoutingModule,
		BrowserAnimationsModule,
		HttpClientModule,
		FormsModule,
		HammerModule,
		NgbModule,
		ReactiveFormsModule,
		NgbCarouselModule,
		IntercomModule.forRoot({
			appId: 'xmrpz3q4',
			updateOnRouterChange: true,
		}),
		MatIconModule,
		MatFormFieldModule,
		MatInputModule,
		MatButtonModule,
		MatDividerModule,
		MatListModule,
		MatMenuModule,
		MatSnackBarModule,
		MatToolbarModule,
		MatDialogModule,
		MatProgressBarModule,
		MatProgressSpinnerModule,
		MatNativeDateModule,
		MatDatepickerModule,
		MatExpansionModule,
		MatSelectModule,
		MatTabsModule,
		MatTooltipModule,
		ClipboardModule,
		MatRadioModule,
		MatButtonToggleModule,
		MatTableModule,
		MatCheckboxModule,
		MatStepperModule,
		ShareButtonsModule.withConfig({
			debug: true,
		}),
		ShareIconsModule,
		// Google Adsense
		GoogleAdModule,
		// Auth
		AuthLayoutModule,
		AuthDialogModule,
		// Ad item
		AdItemModule,
		AdItemV2Module,
		AdItemBannerModule,
		// Filter
		FilterModule,
		// ButtonModule
		ButtonModule,
		// Ads banner
		AdsBannerModule,
		BoostAdDialogModule,
		SharedModule,
		VideoModalModule,
	],
})
export class AppModule {}
